import React from "react";
import { Link } from "react-router-dom";
import {
  Wine, ChefHat, Hotel, Tv, Crown, Award, BookOpen, Sparkles, ShoppingBag,
  Tag, Users, LineChart, Megaphone, Gavel, FileDown, Globe, ArrowRight,
  CheckCircle2, ShieldCheck, Languages, Building2, Briefcase, Camera,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATIC = (path) => `${API}${path}`;

export default function Pitch() {
  return (
    <div data-testid="pitch-page" className="bg-[#0a0a0a] text-white min-h-screen">
      {/* COVER */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img src={STATIC("/api/static/heroes/club_hero.png")} alt="" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/30 to-[#7A1C2B]/40" />
        <div className="relative container-xl py-20 md:py-32">
          <div className="text-[10px] md:text-xs uppercase tracking-[0.5em] text-[#C9A961] mb-6 flex items-center gap-3 animate-fade-in-up">
            <span className="h-px w-16 bg-[#C9A961]" /> Investor & Partner Pitch · 2026
          </div>
          <h1 className="editorial-heading text-6xl md:text-8xl lg:text-9xl leading-[0.9] animate-fade-in-up animation-delay-150">
            Amazonico<br /><span className="italic text-[#C9A961]">3000.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-2xl text-white/90 leading-relaxed animate-fade-in-up animation-delay-300">
            La piattaforma italiana del vino, del food gourmet, delle esperienze e dei servizi premium. Un ecosistema unico per consumatori, rivenditori, franchisee e investitori.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 animate-fade-in-up animation-delay-500">
            <a href={`${API}/api/pitch/deck.pdf`} target="_blank" rel="noreferrer" data-testid="pitch-download-pdf" className="inline-flex items-center gap-2 px-7 h-12 bg-[#C9A961] text-black rounded-sm font-semibold uppercase tracking-wider text-xs hover:bg-white transition-colors">
              <FileDown className="h-4 w-4" /> Scarica il pitch PDF
            </a>
            <Link to="/" className="inline-flex items-center gap-2 px-7 h-12 bg-white/5 border border-white/40 text-white rounded-sm hover:bg-white hover:text-black transition-colors uppercase tracking-wider text-xs backdrop-blur">
              Esplora il sito <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
            <Stat n="1.200+" l="Etichette di vino" />
            <Stat n="50+" l="Cantine partner" />
            <Stat n="6" l="Lingue" />
            <Stat n="11" l="Paesi servirti" />
          </div>
        </div>
      </section>

      {/* PROBLEM / VISION */}
      <Section eyebrow="La Vision" title="Un solo brand, un mondo intero del vino italiano">
        <div className="grid md:grid-cols-3 gap-6">
          <ProblemCard icon={Languages} title="Frammentazione" desc="Il consumatore italiano del vino è spezzato fra enoteche fisiche, marketplace generici, riviste cartacee, club esclusivi. Nessun brand riunisce tutto." />
          <ProblemCard icon={Wine} title="Scarsa accessibilità B2B" desc="Rivenditori e Ho.Re.Ca. faticano a trovare un marketplace digitale italiano serio, con anagrafica integrata, lotti, aste e marketing." />
          <ProblemCard icon={Globe} title="Vuoto premium internazionale" desc="L'Italia esporta vino ma non ha un brand premium retail multilingua all'altezza dell'esperienza. Amazonico 3000 chiude il gap." />
        </div>
      </Section>

      {/* B2C UNIVERSE */}
      <BigImageSection
        eyebrow="Universo B2C"
        title="Il vino, a casa tua"
        subtitle="Una destinazione cinematografica per il consumatore italiano che ama vino, cucina, esperienze e cultura."
        image={STATIC("/api/static/tiles/tile_vini.png")}
      />

      {/* B2C feature grid */}
      <section className="container-xl py-16 md:py-24 bg-[#0a0a0a]">
        <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">Cosa offre</div>
        <h2 className="editorial-heading text-4xl md:text-5xl mb-10">8 funzioni B2C live</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <FeatureCard icon={Wine} title="Catalogo Vini" desc="1.200+ etichette filtrate per regione, tipologia, annata, prezzo." path="/vini" image={STATIC("/api/static/heroes/vini_hero.png")} />
          <FeatureCard icon={ChefHat} title="Food Box" desc="Box gourmet stagionali, regionali e a tema regalo." path="/box" image={STATIC("/api/static/heroes/box_hero.png")} />
          <FeatureCard icon={Hotel} title="Esperienze" desc="Cantine, vendemmie, weekend in resort 5 stelle." path="/esperienze" image={STATIC("/api/static/heroes/esperienze_hero.png")} />
          <FeatureCard icon={Tv} title="Amazonico TV" desc="Reportage video dentro le cantine più iconiche." path="/tv" image={STATIC("/api/static/heroes/tv_hero.png")} />
          <FeatureCard icon={Crown} title="Club Privilegia" desc="Tier Argento/Oro/Platino con auto, chef privato, viaggi." path="/club" image={STATIC("/api/static/heroes/club_hero.png")} />
          <FeatureCard icon={BookOpen} title="Magazine" desc="Rivista trimestrale di vino, cucina e cultura italiana." path="/rivista" image={STATIC("/api/static/magazine/cover_winter-2026.png")} />
          <FeatureCard icon={Sparkles} title="Punti Premio" desc="1€ = 1 punto. Riscatta box, esperienze, weekend." path="/punti" image={STATIC("/api/static/rewards/rewards_card.png")} />
          <FeatureCard icon={ChefHat} title="AI Ricette + Sommelier" desc="Ricette generate con GPT, abbinamento vino con AI." path="/ai-ricette" />
        </div>
      </section>

      {/* CLUB SPOTLIGHT */}
      <section className="relative overflow-hidden py-24 md:py-32" data-testid="pitch-club">
        <img src={STATIC("/api/static/heroes/club_hero.png")} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0608]/80 via-black/55 to-[#3A050D]/70" />
        <div className="relative container-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">Premium recurring revenue</div>
          <h2 className="editorial-heading text-4xl md:text-6xl mb-3">Club Privilegia</h2>
          <p className="text-white/85 max-w-2xl mb-10 text-lg">3 tier annuali con servizi esclusivi: aste private, viaggi nelle migliori cantine, masterclass con i produttori. Per i tier alti: auto di lusso con autista e chef privato per cene fino a 8 ospiti.</p>
          <div className="grid md:grid-cols-3 gap-5">
            <TierCard color="#C9C9C9" name="Argento" price="€490" perks={["Welcome box 3 bottiglie", "10% sconto B2C", "Magazine 4 numeri", "Eventi prioritari"]} />
            <TierCard color="#C9A961" name="Oro" price="€1.490" highlight perks={["Tutto Argento", "Viaggio in cantina/anno", "Aste anticipate 48h", "Auto di lusso 1 evento", "Chef privato cena 8 ospiti", "Masterclass trimestrali"]} />
            <TierCard color="#1E1E1E" name="Platino" price="€4.900" perks={["Tutto Oro", "2 viaggi top tenute", "Aste senza tetto", "Sommelier dedicato", "Auto lusso illimitato IT", "Chef privato 4 cene/anno", "Concierge wine 24/7"]} />
          </div>
        </div>
      </section>

      {/* B2B UNIVERSE */}
      <BigImageSection
        eyebrow="Universo B2B"
        title="Il marketplace italiano del vino"
        subtitle="Un portale completo per rivenditori, broker, importatori, Ho.Re.Ca. e GDO. Con anagrafica certificata, lotti peer-to-peer, aste, marketing tools."
        image={STATIC("/api/static/tiles/tile_box.png")}
        reverse
      />

      <section className="container-xl py-16 md:py-24 bg-[#0a0a0a]">
        <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">B2B Backend</div>
        <h2 className="editorial-heading text-4xl md:text-5xl mb-10">7 strumenti per il rivenditore</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <FeatureCard icon={Building2} title="Anagrafica certificata" desc="P.IVA, HACCP, accise, IBAN, zone consegna multiselect." compact />
          <FeatureCard icon={Tag} title="Vendi lotto" desc="Pubblica vini/foodbox in lotti — workflow approvazione admin." compact />
          <FeatureCard icon={ShoppingBag} title="Marketplace lotti" desc="Acquista lotti peer-to-peer da altri rivenditori certificati." compact />
          <FeatureCard icon={Wine} title="Catalogo Amazonico" desc="1.200+ etichette con prezzi B2B dinamici, MOQ, incoterms." compact />
          <FeatureCard icon={Megaphone} title="Marketing campaigns" desc="Crea campagne social/print/TV con budget e tracking." compact />
          <FeatureCard icon={Gavel} title="Aste private" desc="Proponi lotti d'asta con base price, durata, riserva." compact />
          <FeatureCard icon={Sparkles} title="AI Sommelier B2B" desc="Abbinamento vino-piatto, schede tecniche, story produttore." compact />
        </div>
      </section>

      {/* FRANCHISING */}
      <section className="relative overflow-hidden py-24 md:py-32" data-testid="pitch-franchising">
        <img src={STATIC("/api/static/tiles/franchising_storefront.png")} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0608]/85 via-[#1a0608]/55 to-transparent" />
        <div className="relative container-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3 flex items-center gap-3">
            <Globe className="h-3.5 w-3.5" /> Franchising · Network 2026
          </div>
          <h2 className="editorial-heading text-4xl md:text-6xl mb-4">Apri il tuo Amazonico</h2>
          <p className="text-white/85 max-w-2xl text-lg mb-8">Format chiavi in mano: enoteca + food + esperienze + AI Sommelier. Brand premium, training, supply chain, marketing centralizzato. 30 giorni per essere operativi.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl">
            <Pillar icon={CheckCircle2} text="Investimento da €120K" />
            <Pillar icon={CheckCircle2} text="Royalty trasparenti" />
            <Pillar icon={CheckCircle2} text="Training 4 settimane" />
            <Pillar icon={CheckCircle2} text="Supply chain inclusa" />
          </div>
        </div>
      </section>

      {/* SUPER ADMIN */}
      <section className="container-xl py-16 md:py-24">
        <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">Super Admin</div>
        <h2 className="editorial-heading text-4xl md:text-5xl mb-3">Operations centralizzate</h2>
        <p className="text-white/70 max-w-2xl mb-10">Una console Super Admin per gestire ogni flusso del business: 16 sezioni live con badge real-time, audit log, controllo qualità.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          {[
            ["Customers", Users], ["Brokers/Buyer", Briefcase], ["Vini & Box", Wine],
            ["Lotti B2B", Tag], ["Aste", Gavel], ["Franchising", Award],
            ["Marketing Hub", Megaphone], ["Media Kit", Camera], ["Magazine & Punti", BookOpen],
            ["Newsletter", FileDown], ["Analytics", LineChart], ["Audit log", ShieldCheck],
          ].map(([label, Icon]) => (
            <div key={label} className="flex items-center gap-3 p-4 border border-white/10 rounded-sm bg-white/5">
              <Icon className="h-4 w-4 text-[#C9A961] shrink-0" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TECH STACK */}
      <section className="bg-[#0f0f0f] border-y border-white/10 py-16">
        <div className="container-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">Tech & AI</div>
          <h2 className="editorial-heading text-3xl md:text-4xl mb-6">AI-first, multilingua, deploy production</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <TechCard label="Frontend" v="React + Tailwind + Shadcn" />
            <TechCard label="Backend" v="FastAPI + MongoDB" />
            <TechCard label="Pagamenti" v="Stripe (carta + abbonamenti)" />
            <TechCard label="AI" v="GPT-5.2 + Gemini Nano Banana" />
            <TechCard label="i18n" v="6 lingue (IT/EN/DE/FR/ES/PT)" />
            <TechCard label="Hosting" v="Production live · domain proprietario" />
            <TechCard label="GDPR" v="Cookie banner, consensi, audit" />
            <TechCard label="SEO" v="OG meta, OG cover dinamico" />
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden py-24 md:py-32 bg-gradient-to-br from-[#7A1C2B] via-[#5A0F1F] to-[#3A050D]">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #C9A961 0%, transparent 50%), radial-gradient(circle at 80% 70%, #E2A03F 0%, transparent 50%)" }} />
        <div className="relative container-xl text-center">
          <Crown className="h-12 w-12 text-[#C9A961] mx-auto mb-5" />
          <h2 className="editorial-heading text-5xl md:text-7xl mb-5">
            Costruiamo insieme<br /><span className="italic text-[#C9A961]">il futuro del vino italiano.</span>
          </h2>
          <p className="text-white/85 max-w-2xl mx-auto text-lg mb-10">
            Cerchiamo investitori, partner B2B, franchisee e talent. Una piattaforma viva, già monetizzata, multilingua, pronta a scalare in EU + LATAM.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:hello@amazonico3000.com" data-testid="pitch-contact" className="inline-flex items-center gap-2 px-8 h-14 bg-[#C9A961] text-black rounded-sm font-bold uppercase tracking-wider text-sm hover:bg-white transition-colors">
              <Globe className="h-4 w-4" /> Contattaci
            </a>
            <a href={`${API}/api/pitch/deck.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-8 h-14 bg-white/10 border border-white/40 text-white rounded-sm hover:bg-white hover:text-black transition-colors uppercase tracking-wider text-sm backdrop-blur">
              <FileDown className="h-4 w-4" /> Pitch PDF
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────
function Stat({ n, l }) {
  return (
    <div>
      <div className="editorial-heading text-3xl md:text-4xl text-[#C9A961]">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mt-1">{l}</div>
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="container-xl py-16 md:py-24">
      <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">{eyebrow}</div>
      <h2 className="editorial-heading text-4xl md:text-5xl mb-10">{title}</h2>
      {children}
    </section>
  );
}

function ProblemCard({ icon: Icon, title, desc }) {
  return (
    <div className="p-6 border border-white/10 rounded-sm bg-white/5">
      <Icon className="h-7 w-7 text-[#C9A961] mb-3" />
      <div className="editorial-heading text-xl mb-2">{title}</div>
      <p className="text-sm text-white/70">{desc}</p>
    </div>
  );
}

function BigImageSection({ eyebrow, title, subtitle, image, reverse }) {
  return (
    <section className={`grid md:grid-cols-2 ${reverse ? "" : ""} bg-[#0a0a0a] border-y border-white/5`} data-testid="pitch-big-section">
      <div className={`p-10 md:p-16 lg:p-24 flex flex-col justify-center ${reverse ? "md:order-2" : ""}`}>
        <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">{eyebrow}</div>
        <h2 className="editorial-heading text-4xl md:text-5xl lg:text-6xl mb-4">{title}</h2>
        <p className="text-white/80 text-lg leading-relaxed">{subtitle}</p>
      </div>
      <div className={`relative min-h-[320px] md:min-h-[480px] ${reverse ? "md:order-1" : ""}`}>
        <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, desc, path, image, compact }) {
  const Body = (
    <div className={`group bg-white/5 border border-white/10 rounded-sm overflow-hidden hover:border-[#C9A961]/50 transition-colors h-full flex flex-col`}>
      {!compact && image && (
        <div className="aspect-[16/10] bg-[#0a0a0a] relative overflow-hidden">
          <img src={image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <Icon className="h-5 w-5 text-[#C9A961] mb-3" />
        <div className="font-semibold text-base mb-1">{title}</div>
        <p className="text-sm text-white/70 flex-1">{desc}</p>
        {path && (
          <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#C9A961] group-hover:gap-2 transition-all">
            Esplora <ArrowRight className="h-3 w-3" />
          </span>
        )}
      </div>
    </div>
  );
  return path ? <Link to={path}>{Body}</Link> : Body;
}

function TierCard({ color, name, price, perks, highlight }) {
  return (
    <div className={`bg-white/5 backdrop-blur border ${highlight ? "border-[#C9A961]" : "border-white/10"} rounded-sm p-6`}>
      <div className="h-1 w-12 mb-4" style={{ background: color }} />
      <div className="flex items-baseline gap-2 mb-1">
        <h3 className="editorial-heading text-3xl">{name}</h3>
        {highlight && <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-[#C9A961] text-black rounded-full font-bold">Best</span>}
      </div>
      <div className="text-2xl font-bold mb-4">{price}<span className="text-xs text-white/50 font-normal"> /anno</span></div>
      <ul className="space-y-2 text-sm text-white/80">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#C9A961] shrink-0 mt-0.5" /> {p}</li>
        ))}
      </ul>
    </div>
  );
}

function Pillar({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-2.5">
      <Icon className="h-4 w-4 text-[#C9A961] shrink-0" /> {text}
    </div>
  );
}

function TechCard({ label, v }) {
  return (
    <div className="p-4 border border-white/10 rounded-sm bg-white/5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]/80 mb-1">{label}</div>
      <div className="font-semibold">{v}</div>
    </div>
  );
}
